import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClient, QueryClientProvider } from "react-query";
import { UserbackProvider } from "@userback/react";
import { HelmetProvider } from "react-helmet-async";
import Modal from "react-modal";
import App from "./App";
import UserbackIdentify, { toUserbackUser } from "./components/Userback/UserbackIdentify";
import { store } from "./app/store";
import reportWebVitals from "./reportWebVitals";
import { setupSessionExpiryHandling } from "./utils/sessionExpiry";

import "./config";
import "./index.css";
import "./assets/custom.css";

// Userback feedback widget. Set REACT_APP_USERBACK_TOKEN per environment so
// staging/prod feedback routes to the right Userback project. Missing token
// means the widget simply does not mount.
const USERBACK_TOKEN = process.env.REACT_APP_USERBACK_TOKEN;

// Identify an already logged-in user at init. If Userback first loads
// anonymously, the identify() call that follows makes it destroy and rebuild
// the launcher, which flickers at the screen edge on every page load.
const initialUserbackUser = toUserbackUser(store.getState().auth.user);
const USERBACK_OPTIONS = initialUserbackUser ? { user_data: initialUserbackUser } : {};

// Dozens of components across the app do `JSON.parse(localStorage.getItem("settings"))`
// with no fallback, then read a field straight off the result (e.g. `.currency`).
// Whenever that key is missing — right after a logout/localStorage.clear(), or a
// fresh session before the settings API call has written it — JSON.parse(null)
// returns null and the very next property read throws, crashing the whole React
// tree (looks exactly like an unexplained logout/blank page). Guaranteeing the
// key always holds valid JSON before anything renders closes that off everywhere
// at once, without touching every one of those call sites individually.
try {
  const rawSettings = localStorage.getItem("settings");
  if (rawSettings) {
    JSON.parse(rawSettings);
  } else {
    localStorage.setItem("settings", "{}");
  }
} catch (e) {
  localStorage.setItem("settings", "{}");
}

// After a frontend redeploy, a tab that's been open since before the deploy
// still holds JS chunk hashes that no longer exist on the server. The next
// route-based lazy import for that tab 404s, and the app is stuck broken
// until the user manually refreshes. Auto-reload once when that happens so
// the tab picks up the new build itself. The sessionStorage guard stops a
// reload loop if the chunk is missing for some other persistent reason.
window.addEventListener("error", (event) => {
  const message = event?.message || event?.error?.message || "";
  if (/loading chunk .* failed/i.test(message) || /failed to fetch dynamically imported module/i.test(message)) {
    if (!sessionStorage.getItem("chunkReloadAttempted")) {
      sessionStorage.setItem("chunkReloadAttempted", "1");
      window.location.reload();
    }
  }
});
window.addEventListener("unhandledrejection", (event) => {
  const message = event?.reason?.message || "";
  if (/loading chunk .* failed/i.test(message) || /failed to fetch dynamically imported module/i.test(message)) {
    if (!sessionStorage.getItem("chunkReloadAttempted")) {
      sessionStorage.setItem("chunkReloadAttempted", "1");
      window.location.reload();
    }
  }
});

// "ResizeObserver loop completed with undelivered notifications" is a spec-defined
// browser behavior: if a ResizeObserver callback itself causes another resize
// within the same observation cycle (e.g. rsuite's date-range picker positioning
// its popup on open), the browser reports a loop error once the iteration limit
// is hit. Trying to catch/suppress that report after the fact is a losing race
// against CRA's dev-mode error overlay, which registers its own handler before
// this app's code runs and can intercept it through more than one path.
// The real, standard fix: stop the condition from ever occurring by deferring
// every ResizeObserver callback to the next animation frame, so it never runs
// synchronously inside the resize-observation cycle that triggers the loop
// check in the first place. This patches the native constructor globally, so
// every ResizeObserver used anywhere in the app (rsuite, the `resize-observer`
// polyfill fallback, etc.) is covered without touching each call site.
(function patchResizeObserverLoop() {
  if (typeof window === "undefined" || !window.ResizeObserver) return;
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
})();

Modal.setAppElement("#root");
Modal.defaultProps = {
  ...Modal.defaultProps,
  closeTimeoutMS: 420,
  bodyOpenClassName: "",
  htmlOpenClassName: "",
  parentSelector: () => document.getElementById("content-modal-root") || document.body,
};

// Create a new QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Log out automatically when the server rejects the session (expired/invalid token)
setupSessionExpiryHandling({ store, queryClient });

// Get the root container from the DOM
const container = document.getElementById("root");

// Ensure the container exists before creating the root
if (container) {
  const root = createRoot(container);
  console.warn = () => { };

  // Render the application
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            {USERBACK_TOKEN ? (
              <UserbackProvider token={USERBACK_TOKEN} options={USERBACK_OPTIONS}>
                <UserbackIdentify />
                <App />
              </UserbackProvider>
            ) : (
              <App />
            )}
          </Provider>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>
  );

  // Measure performance of the app
  reportWebVitals();
} else {
  console.error("Root container not found. Failed to render the application.");
}